import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';

import { navigation, type NavItem } from '@/app/navigation';
import { useNavCounters, useSession } from '@/shared/api/queries';
import type { NavCounters } from '@/shared/domain/selectors';

import { GroupIcon } from './GroupIcon';
import styles from './SideNav.module.css';

function countFor(item: NavItem, counters: NavCounters | null): number | null {
  if (!item.count || !counters) return null;
  return counters[item.count];
}

/* An entry with no `capability` is for everyone; one with a capability is shown
   only where the session carries it.
 *
 * The session is the only source. There is no map from role to capability in the
 * cockpit and there should not be — the service works that out from the `roles`
 * claim and reports the result, so the sidebar and the API cannot disagree about
 * what an operator may do.
 *
 * While the session is still loading nothing capability-gated is drawn. An entry
 * that appears a moment after the rest reads as the cockpit changing its mind;
 * an entry that was never there and then arrives once is just the page finishing
 * loading. */
function permitted(item: NavItem, capabilities: readonly string[] | undefined): boolean {
  if (item.capability === undefined) return true;
  return capabilities?.includes(item.capability) === true;
}

export function SideNav(): ReactElement {
  const counters = useNavCounters();
  const session = useSession();
  const { t } = useTranslation(['nav', 'domain']);

  const capabilities = session.data?.capabilities;

  return (
    <nav className={styles.sidenav} aria-label={t('nav:primary')}>
      {navigation.map((group) => {
        const items = group.items.filter((item) => permitted(item, capabilities));

        /* A heading with nothing under it is worse than no heading: it says a
           section exists and then withholds it. */
        if (items.length === 0) return null;

        return (
          <div key={group.groupKey}>
            <div className={styles.group}>{t(`nav:groups.${group.groupKey}`)}</div>
            {items.map((item) => {
              const count = countFor(item, counters);

              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.end ?? false}
                  className={({ isActive }) =>
                    isActive ? `${styles.item} ${styles.active}` : styles.item
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span className={styles.label}>
                        <span className={styles.icon}>
                          <GroupIcon name={group.icon} />
                        </span>
                        {t(item.labelKey)}
                      </span>
                      {count === null ? null : (
                        <span
                          /* The alert tint is earned, not permanent: a queue at zero
                             is not a problem and should not read like one. */
                          className={
                            item.tone === 'alert' && count > 0 && !isActive
                              ? `${styles.count} ${styles.alert}`
                              : styles.count
                          }
                        >
                          {count}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              );
            })}
          </div>
        );
      })}
    </nav>
  );
}
